/**
 * Categorical colour palette for the standings-over-time chart.
 *
 * 20 distinct, reasonably colour-blind-friendly hues with light/dark-mode variants,
 * following the `{ light, dark }` convention in components/worldMap/mapColors.ts.
 * Colours are assigned deterministically by series order (rank), so a given user keeps
 * the same colour across re-renders within a session.
 */
export interface ChartColor {
  light: string;
  dark: string;
}

/**
 * Palette ordered for maximum adjacent contrast. Derived from the Tol / Okabe-Ito
 * qualitative families, lightened slightly for dark mode legibility on a deep navy bg.
 */
export const CHART_PALETTE: ChartColor[] = [
  { light: '#2F6FED', dark: '#5B8DEF' }, // blue
  { light: '#E8590C', dark: '#FF922B' }, // orange
  { light: '#2B8A3E', dark: '#51CF66' }, // green
  { light: '#C2255C', dark: '#F06595' }, // magenta
  { light: '#9C36B5', dark: '#CC5DE8' }, // purple
  { light: '#0C8599', dark: '#3BC9DB' }, // teal
  { light: '#E03131', dark: '#FF6B6B' }, // red
  { light: '#5C940D', dark: '#A9E34B' }, // lime
  { light: '#1864AB', dark: '#74C0FC' }, // deep blue
  { light: '#D9480F', dark: '#FFA94D' }, // burnt orange
  { light: '#087F5B', dark: '#38D9A9' }, // emerald
  { light: '#A61E4D', dark: '#FAA2C1' }, // raspberry
  { light: '#6741D9', dark: '#9775FA' }, // indigo
  { light: '#0B7285', dark: '#66D9E8' }, // cyan
  { light: '#B08900', dark: '#FFD43B' }, // gold
  { light: '#495057', dark: '#CED4DA' }, // slate/grey
  { light: '#862E9C', dark: '#E599F7' }, // violet
  { light: '#2F9E44', dark: '#69DB7C' }, // forest
  { light: '#1971C2', dark: '#4DABF7' }, // sky
  { light: '#E8590C', dark: '#FFC078' }, // amber
];

/** Resolve a stable colour for a series index in the current colour mode. */
export function chartColor(index: number, colorMode: 'light' | 'dark'): string {
  const entry = CHART_PALETTE[index % CHART_PALETTE.length];
  return entry[colorMode];
}

/** Map a list of uids (in series order) to a stable uid -> colour-index lookup. */
export function buildColorIndex(uidsInOrder: string[]): Map<string, number> {
  const map = new Map<string, number>();
  uidsInOrder.forEach((uid, i) => map.set(uid, i));
  return map;
}
